import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Save, X, Trash2, Building2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface Estate {
  id: number;
  name: string;
  nameEn?: string;
  region: 'NT_KL' | 'HK';
  area?: string;
  managementCompany?: string;
  minOrderAmount: number;
  deliveryDay?: string;
  isActive: number;
  sortOrder: number;
}

const regionOptions = [
  { value: 'NT_KL', label: '新界 / 九龍' },
  { value: 'HK', label: '香港島' },
];

const AdminEstates: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWriteEstates = can(PERMISSIONS.ESTATES_WRITE);
  const [estates, setEstates] = useState<Estate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const emptyForm = {
    name: '',
    nameEn: '',
    region: 'NT_KL' as 'NT_KL' | 'HK',
    area: '',
    managementCompany: '',
    minOrderAmount: '',
    deliveryDay: '',
    isActive: true,
    sortOrder: '0',
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchEstates();
  }, []);

  const fetchEstates = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/public/admin/service-estates');
      if (res.ok) {
        setEstates(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        setError('獲取屋苑列表失敗');
      }
    } catch {
      setError('獲取屋苑列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 4000);
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setError('請填寫屋苑名稱');
      return;
    }
    const payload = {
      name: formData.name,
      name_en: formData.nameEn || undefined,
      region: formData.region,
      area: formData.area || undefined,
      management_company: formData.managementCompany || undefined,
      min_order_amount: Number(formData.minOrderAmount) || 0,
      delivery_day: formData.deliveryDay || undefined,
      is_active: formData.isActive,
      sort_order: Number(formData.sortOrder) || 0,
    };
    try {
      const res = await apiFetch(
        editingId ? `/api/public/admin/service-estates/${editingId}` : '/api/public/admin/service-estates',
        {
          method: editingId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (res.ok) {
        showMessage(editingId ? '屋苑已更新' : '屋苑已新增');
        resetForm();
        fetchEstates();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '儲存失敗');
      }
    } catch {
      setError('儲存失敗');
    }
  };

  const handleEdit = (estate: Estate) => {
    setEditingId(estate.id);
    setFormData({
      name: estate.name,
      nameEn: estate.nameEn || '',
      region: estate.region,
      area: estate.area || '',
      managementCompany: estate.managementCompany || '',
      minOrderAmount: String(estate.minOrderAmount || 0),
      deliveryDay: estate.deliveryDay || '',
      isActive: estate.isActive === 1,
      sortOrder: String(estate.sortOrder || 0),
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除此屋苑嗎？相關用戶的屋苑權限也會一併移除。')) return;
    try {
      const res = await apiFetch(`/api/public/admin/service-estates/${id}`, { method: 'DELETE' });
      if (res.ok) {
        showMessage('屋苑已刪除');
        fetchEstates();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '刪除失敗');
      }
    } catch {
      setError('刪除失敗');
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Building2 className="text-orange-600" />
              屋苑管理
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">管理可送貨屋苑及管理公司</p>
          </div>
          {canWriteEstates && (
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
            >
              <Plus size={20} /> 新增屋苑
            </button>
          )}
        </div>

        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {showForm && (
          <div className="mb-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
              {editingId ? '編輯屋苑' : '新增屋苑'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">屋苑名稱 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：Yoho Midtown"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">英文名稱</label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={e => setFormData(prev => ({ ...prev, nameEn: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">地區 *</label>
                <select
                  value={formData.region}
                  onChange={e => setFormData(prev => ({ ...prev, region: e.target.value as 'NT_KL' | 'HK' }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {regionOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">分區</label>
                <input
                  type="text"
                  value={formData.area}
                  onChange={e => setFormData(prev => ({ ...prev, area: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：元朗"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">管理公司</label>
                <input
                  type="text"
                  value={formData.managementCompany}
                  onChange={e => setFormData(prev => ({ ...prev, managementCompany: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：元朗物業管理"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">最低消費（HK$）</label>
                <input
                  type="number"
                  min={0}
                  value={formData.minOrderAmount}
                  onChange={e => setFormData(prev => ({ ...prev, minOrderAmount: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="0 代表使用地區預設"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">可送貨日</label>
                <input
                  type="text"
                  value={formData.deliveryDay}
                  onChange={e => setFormData(prev => ({ ...prev, deliveryDay: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：星期二、四、六"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={e => setFormData(prev => ({ ...prev, sortOrder: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <input
                  id="estate-active"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                />
                <label htmlFor="estate-active" className="text-sm text-gray-700 dark:text-gray-300">啟用</label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
              >
                <Save size={18} /> 儲存
              </button>
              <button
                onClick={resetForm}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-bold rounded-lg transition"
              >
                <X size={18} /> 取消
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-gray-600 dark:text-gray-400">載入中...</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">名稱</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">地區</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">分區</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">管理公司</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">最低消費</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">狀態</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {estates.map(estate => (
                    <tr key={estate.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{estate.name}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {estate.region === 'HK' ? '香港島' : '新界 / 九龍'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.area || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.managementCompany || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {estate.minOrderAmount > 0 ? `HK$${estate.minOrderAmount}` : '地區預設'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${estate.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                          {estate.isActive ? '啟用' : '停用'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canWriteEstates ? (
                          <div className="inline-flex gap-2">
                            <button
                              onClick={() => handleEdit(estate)}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition"
                              title="編輯"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(estate.id)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                              title="刪除"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {estates.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        暫時沒有屋苑，請按「新增屋苑」建立。
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEstates;
